import React, { useCallback, useEffect, useState } from "react";

import Modal from "./Modal";
import useReveal from "../hooks/useReveal";

// The published hiring sheet — configured per environment, see the README.
const SHEET_URL = process.env.REACT_APP_JOBS_SHEET_URL || "";

const RAZORPAY_SDK = "https://checkout.razorpay.com/v1/checkout.js";
const APPLICATION_FEE = Number(process.env.REACT_APP_APPLICATION_FEE || 1); // in rupees

// Loads the Razorpay checkout script once and resolves when it is usable.
// A script that fails is removed from the document, so a later attempt injects
// a fresh one instead of attaching listeners to a tag that will never fire
// again — otherwise the Pay button would wait forever after one bad load.
function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }

    const existing = document.querySelector(`script[src="${RAZORPAY_SDK}"]`);
    const script = existing || document.createElement("script");

    const cleanup = () => {
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
    };

    function onLoad() {
      cleanup();
      if (window.Razorpay) resolve(window.Razorpay);
      else reject(new Error("Razorpay SDK failed to load"));
    }

    function onError() {
      cleanup();
      script.remove();
      reject(new Error("Razorpay SDK failed to load"));
    }

    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);

    if (!existing) {
      script.src = RAZORPAY_SDK;
      script.async = true;
      document.body.appendChild(script);
    }
  });
}

function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [selectedJob, setSelectedJob] = useState(null);
  const [applicant, setApplicant] = useState({ name: "", email: "", phone: "" });
  const [formError, setFormError] = useState("");
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null); // { ok, title, message }

  const [razorpayKeyId, setRazorpayKeyId] = useState("");

  useEffect(() => {
    let cancelled = false;

    if (!SHEET_URL) {
      setLoadError("The job feed is not configured for this environment.");
      setLoading(false);
      return undefined;
    }

    fetch(SHEET_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`Job feed responded ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setJobs(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("Error fetching jobs:", err);
        setLoadError("We could not load the current openings. Please try again shortly.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Razorpay key is served at runtime by the backend so it never sits in the bundle.
    fetch("/api/config")
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("config unavailable"))))
      .then((data) => {
        if (!cancelled) setRazorpayKeyId(data.razorpayKeyId || "");
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Unable to load config:", err);
      });

    loadRazorpay().catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useReveal([jobs.length, loading]);

  const openApply = (job) => {
    setSelectedJob(job);
    setApplicant({ name: "", email: "", phone: "" });
    setFormError("");
  };

  const closeApply = useCallback(() => {
    setSelectedJob(null);
    setPaying(false);
  }, []);

  const handleField = (e) => {
    const { name, value } = e.target;
    setApplicant((prev) => ({ ...prev, [name]: value }));
  };

  const handlePay = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!applicant.name || !applicant.email || !applicant.phone) {
      setFormError("Please fill all required fields.");
      return;
    }
    if (!razorpayKeyId) {
      setFormError("Payment configuration is not available yet. Please try again.");
      return;
    }

    setPaying(true);

    try {
      const Razorpay = await loadRazorpay();

      const orderResponse = await fetch("/api/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: APPLICATION_FEE,
          user: {
            name: applicant.name,
            email: applicant.email,
            phone: applicant.phone,
            jobTitle: selectedJob.title,
          },
        }),
      });

      if (!orderResponse.ok) throw new Error("Failed to create payment order");

      const orderData = await orderResponse.json();
      if (!orderData?.orderId || !orderData?.amount) {
        throw new Error("Invalid order data received from server");
      }

      const rzp = new Razorpay({
        // v1 passed an undeclared RAZORPAY_KEY_ID here, which threw before
        // checkout could open. This uses the key fetched from /api/config.
        key: razorpayKeyId,
        amount: orderData.amount, // already in paise from the backend
        currency: "INR",
        name: "Job Application",
        description: `Application for ${selectedJob.title}`,
        order_id: orderData.orderId,
        handler: async (response) => {
          try {
            const verification = await fetch("/api/verify-payment", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                orderId: orderData.orderId,
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature,
                status: "success",
                userData: {
                  name: applicant.name,
                  email: applicant.email,
                  phone: applicant.phone,
                  jobId: selectedJob.id,
                  jobTitle: selectedJob.title,
                },
              }),
            });

            if (!verification.ok) {
              const error = await verification.json().catch(() => ({}));
              throw new Error(error.message || "Payment verification failed");
            }

            closeApply();
            setResult({
              ok: true,
              title: "Application received",
              message: `Payment successful. Your reference ID is ${response.razorpay_payment_id}. We will be in touch by email.`,
            });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Verification error:", error);
            closeApply();
            setResult({
              ok: false,
              title: "Payment received, verification pending",
              message: `Your payment went through but we could not verify it automatically. Please contact us quoting reference ID ${response.razorpay_payment_id}.`,
            });
          }
        },
        prefill: {
          name: applicant.name,
          email: applicant.email,
          contact: applicant.phone,
        },
        notes: {
          jobTitle: selectedJob.title,
          applicationDate: new Date().toISOString(),
        },
        theme: { color: "#0640DC" },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });

      rzp.on("payment.failed", (response) => {
        // eslint-disable-next-line no-console
        console.error("Payment failed:", response.error);
        setPaying(false);
        setFormError(`Payment failed: ${response.error?.description || "please try again"}`);
      });

      rzp.open();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Payment error:", error);
      setPaying(false);
      setFormError(`Payment process failed: ${error.message}`);
    }
  };

  return (
    <>
      <section className="hero" style={{ background: "linear-gradient(160deg,#050A1C,#0A1A4E)" }}>
        <div className="wrap" style={{ padding: "68px 0" }}>
          <span className="eyebrow mono">Careers</span>
          <h1 style={{ fontSize: "clamp(30px,4.3vw,52px)", maxWidth: "19ch" }}>
            Build the networks people never have to think about.
          </h1>
          <p className="lede">
            Openings are published live from our hiring sheet. Apply in three fields — the
            application fee is collected securely, and you get a reference number the moment
            payment succeeds.
          </p>
        </div>
      </section>

      <section className="band light" id="Jobs">
        <div className="wrap">
          <div className="head reveal" style={{ marginBottom: 32 }}>
            <span className="mono key">Open roles</span>
            <h2>Current job openings</h2>
          </div>

          {loading && <p className="empty">Loading job openings…</p>}

          {!loading && loadError && <p className="empty">{loadError}</p>}

          {!loading && !loadError && jobs.length === 0 && (
            <p className="empty">No job openings available at the moment.</p>
          )}

          {!loading && !loadError && jobs.length > 0 && (
            <div className="three reveal">
              {jobs.map((job, idx) => (
                <div className="job" key={`${job.title || "role"}-${idx}`}>
                  <h3>{job.title || "Untitled Position"}</h3>
                  <p className="loc">{job.location || "Location not specified"}</p>
                  <p>
                    {job.description?.trim()
                      ? job.description
                      : "No description provided for this role."}
                  </p>
                  {job.department && (
                    <div>
                      <span className="tagm">{job.department}</span>
                    </div>
                  )}
                  <button type="button" className="btn block" onClick={() => openApply(job)}>
                    Apply »
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Application + payment */}
      <Modal
        isOpen={Boolean(selectedJob)}
        onClose={closeApply}
        title={selectedJob ? `Apply for ${selectedJob.title}` : ""}
      >
        <form onSubmit={handlePay} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label htmlFor="ap-name">
            Name
            <input
              id="ap-name"
              className="inp on-light"
              name="name"
              type="text"
              value={applicant.name}
              onChange={handleField}
              required
            />
          </label>
          <label htmlFor="ap-email">
            Email
            <input
              id="ap-email"
              className="inp on-light"
              name="email"
              type="email"
              value={applicant.email}
              onChange={handleField}
              required
            />
          </label>
          <label htmlFor="ap-phone">
            Phone
            <input
              id="ap-phone"
              className="inp on-light"
              name="phone"
              type="tel"
              value={applicant.phone}
              onChange={handleField}
              required
            />
          </label>
          <label htmlFor="ap-fee">
            Application Fee
            <input id="ap-fee" className="inp on-light" value={`₹${APPLICATION_FEE}`} disabled />
          </label>

          {formError && (
            <p role="alert" style={{ color: "var(--crit)", fontSize: 14 }}>
              {formError}
            </p>
          )}

          <button type="submit" className="btn block" disabled={paying}>
            {paying ? "Opening secure checkout…" : `Pay ₹${APPLICATION_FEE} & Apply`}
          </button>
          <p className="mono" style={{ color: "var(--fg-ink-dim)", textAlign: "center", letterSpacing: ".08em" }}>
            Secured by Razorpay Checkout
          </p>
        </form>
      </Modal>

      {/* Outcome */}
      <Modal
        isOpen={Boolean(result)}
        onClose={() => setResult(null)}
        title={result?.title || ""}
      >
        <p style={{ color: "var(--fg-ink-dim)" }}>{result?.message}</p>
        <button
          type="button"
          className="btn"
          style={{ alignSelf: "flex-start" }}
          onClick={() => setResult(null)}
        >
          Close
        </button>
      </Modal>
    </>
  );
}

export default Careers;
